import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatCurrency(amount: number, currency = 'CHF'): string {
  return new Intl.NumberFormat('de-CH', { style: 'currency', currency }).format(amount);
}

export function formatDate(date: string | Date, format: 'short' | 'long' | 'relative' = 'short'): string {
  const d = new Date(date);
  if (format === 'relative') {
    const diff = Date.now() - d.getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
  }
  if (format === 'long') return d.toLocaleDateString('de-CH', { day: '2-digit', month: 'long', year: 'numeric' });
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat('de-CH').format(n);
}

export function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

/**
 * Status is semantic, not identity: it maps onto the theme tokens so it inverts
 * with the rest of the UI. Quiet states stay neutral so the states that need
 * attention are the only coloured chips on screen.
 */
const NEUTRAL_CHIP = 'bg-card-hover text-muted ring-1 ring-inset ring-border';
const ACCENT_CHIP = 'bg-accent-soft text-accent-soft-foreground ring-1 ring-inset ring-accent/30';
const SUCCESS_CHIP = 'bg-success-soft text-success ring-1 ring-inset ring-success/30';
const WARNING_CHIP = 'bg-warning-soft text-warning ring-1 ring-inset ring-warning/30';
const DANGER_CHIP = 'bg-danger-soft text-danger ring-1 ring-inset ring-danger/30';

export const statusColors: Record<string, string> = {
  backlog: NEUTRAL_CHIP,
  todo: NEUTRAL_CHIP,
  in_progress: ACCENT_CHIP,
  review: WARNING_CHIP,
  done: SUCCESS_CHIP,
  draft: NEUTRAL_CHIP,
  active: SUCCESS_CHIP,
  completed: SUCCESS_CHIP,
  archived: NEUTRAL_CHIP,
  pending: WARNING_CHIP,
  approved: SUCCESS_CHIP,
  published: SUCCESS_CHIP,
  rejected: DANGER_CHIP,
};

export const priorityColors: Record<string, string> = {
  low: NEUTRAL_CHIP,
  medium: ACCENT_CHIP,
  high: WARNING_CHIP,
  critical: DANGER_CHIP,
};

export const activityTypeIcons: Record<string, string> = {
  google_ads: 'Search',
  linkedin_ads: 'Linkedin',
  print: 'Newspaper',
  email: 'Mail',
  campaign: 'Megaphone',
  social: 'Share2',
  event: 'Calendar',
  content: 'FileText',
  seo: 'Globe',
  webinar: 'Video',
  other: 'MoreHorizontal',
};
